/**
 * Two lines that are the same text, asked about once (#392).
 *
 * When both readers read the same words but in two different places on the page, the
 * review screen shows two `single_reader` lines with identical text (#391). A learner who
 * wrote the line once would have had to confirm both. This card shows the text ONCE and
 * asks one question - "Is this one line or two?" - and records the answer as the
 * learner's ordinary entries on the two lines, in region order:
 *
 * - **One line**: `confirmed_as_read` on the first, `not_written` on the second.
 * - **Two lines**: `confirmed_as_read` on both.
 * - **Neither**: nothing is recorded; the page shows the two lines separately.
 *
 * The server derives which lines are partners (`identicalPartnerRegionId`) and,
 * because of it, leaves both halves open until the learner answers; the pairing
 * only lets the screen ask one question instead of two. Every entry here goes through the same endpoint as a click on a single line.
 * The rules of `region-review.tsx` hold: the text is untrusted handwriting content
 * rendered as plain text, no box is pre-filled, no reader is picked, and nothing says
 * whether the work is right. If the first entry is recorded and the second fails, the
 * page then shows the two lines separately (one has an entry), so nothing is hidden.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { HomeworkTranscriptionResponse } from "@davinci/contracts";
import { ApiRequestError } from "../../api/client.js";
import { queryKeys } from "../../api/query-keys.js";
import { useApi } from "../../app/api-context.js";
import { FailureNotice } from "../../app/failure-notice.js";
import { useHomeworkLabels } from "../../i18n/homework-labels.js";
import { AnswerLockScope } from "./answer-lock.js";
import { ChoiceRow } from "./choice-row.js";
import { RegionCrop, type PageSize } from "./region-crop.js";
import { HandwrittenText } from "./untrusted-text.js";

type Region = HomeworkTranscriptionResponse["regions"][number];

export interface PairReviewProps {
  /** The pair in region order: `first` comes first on the page. */
  readonly first: Region;
  readonly second: Region;
  /** 1-based positions of the two lines in the list. */
  readonly firstLineNumber: number;
  readonly secondLineNumber: number;
  readonly pageId: string;
  readonly uploadId: string;
  readonly pageSize: PageSize;
  /** "Neither": show the two lines as separate cards. */
  readonly onShowSeparately: () => void;
  /** Presentation only (#619): not the current step of the stepper. The card stays mounted. */
  readonly hidden?: boolean;
  /** Called once the server has recorded the answer (#660): the stepper moves on from there. */
  readonly onMoveOn?: (regionId: string) => void;
  /** The way to the previous step (#660). Absent: no link. */
  readonly onBack?: () => void;
}

export function PairReview({
  first,
  second,
  firstLineNumber,
  secondLineNumber,
  pageId,
  uploadId,
  pageSize,
  onShowSeparately,
  hidden,
  onMoveOn,
  onBack,
}: PairReviewProps): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const queryClient = useQueryClient();

  // The two readers produced the same text (that is what makes them partners); show it once.
  const shown =
    (
      first.primaryReading ??
      first.secondReading ??
      second.primaryReading ??
      second.secondReading
    )?.text ?? "";

  const answer = useMutation({
    // In region order, one after the other: the first entry is on record before the second is sent.
    mutationFn: async (secondKind: "confirmed_as_read" | "not_written") => {
      await api.createHomeworkCorrection(
        pageId,
        first.regionId,
        crypto.randomUUID(),
        { kind: "confirmed_as_read" },
      );
      return api.createHomeworkCorrection(
        pageId,
        second.regionId,
        crypto.randomUUID(),
        { kind: secondKind },
      );
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(
        queryKeys.homeworkTranscription(pageId),
        updated,
      );
      onMoveOn?.(first.regionId);
    },
    // Success or refusal, what is on screen may no longer be what the server holds.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkTranscription(pageId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkUpload(uploadId),
      });
    },
  });

  const failureSentence =
    answer.error instanceof ApiRequestError
      ? t.reviewFailureSentence(answer.error.code) || undefined
      : undefined;

  return (
    <li className="region region-pair" hidden={hidden}>
      <AnswerLockScope active={hidden !== true}>
      <h3>{t.pairHeading(firstLineNumber, secondLineNumber)}</h3>
      <RegionCrop
        pageId={pageId}
        bbox={first.bbox}
        approximate={first.bboxApproximate}
        lineNumber={firstLineNumber}
        text={shown}
        {...pageSize}
      />
      <RegionCrop
        pageId={pageId}
        bbox={second.bbox}
        approximate={second.bboxApproximate}
        lineNumber={secondLineNumber}
        text={shown}
        {...pageSize}
      />
      <p className="region-state">{t.pairExplanation}</p>
      <HandwrittenText text={shown} />
      <div className="region-ask-row">
        <p className="region-question">{t.pairQuestion}</p>
        {onBack === undefined ? null : (
          <button type="button" className="button-link" onClick={onBack}>
            {t.previousLine}
          </button>
        )}
      </div>
      <div className="choice-rows">
        <ChoiceRow
          disabled={answer.isPending}
          onClick={() => answer.mutate("not_written")}
        >
          {t.pairOneLine}
        </ChoiceRow>
        <ChoiceRow
          disabled={answer.isPending}
          onClick={() => answer.mutate("confirmed_as_read")}
        >
          {t.pairTwoLines}
        </ChoiceRow>
        <ChoiceRow disabled={answer.isPending} onClick={onShowSeparately}>
          {t.pairNeither}
        </ChoiceRow>
      </div>

      {answer.isError ? (
        failureSentence !== undefined ? (
          <div className="notice notice-error" role="alert">
            <p>{failureSentence}</p>
          </div>
        ) : (
          <FailureNotice error={answer.error} />
        )
      ) : null}
      </AnswerLockScope>
    </li>
  );
}
